import {
  defaultWorkspaceApiClient,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { Service } from '@toeverything/infra';

import type { FolderPermissions, FolderRole } from './folder-api';

export type WorkspaceAreaKind = 'public' | 'teamspace' | 'private' | 'app';

export interface WorkspaceAreaRef {
  effectiveRole: FolderRole;
  immutable: boolean;
  kind: WorkspaceAreaKind;
  name: string;
  ownerUserId: string | null;
  permissions: FolderPermissions;
  rootFolderId: string;
  systemKind: string | null;
  systemManaged: boolean;
  visibility: 'public' | 'private';
  workspaceId: string;
}

interface WorkspaceAreaListResponse {
  areas: WorkspaceAreaRef[];
}

export interface WorkspaceAreaBootstrap {
  areas: WorkspaceAreaRef[];
  manageableAreas: WorkspaceAreaRef[];
}

interface WorkspaceAreaResponse {
  area: WorkspaceAreaRef | null;
}

export class WorkspaceAreaApiService extends Service {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  async listAreas(workspaceId: string): Promise<WorkspaceAreaRef[]> {
    const response = await this.fetch(workspaceId, '/areas', {
      method: 'GET',
    });
    const payload = (await response.json()) as WorkspaceAreaListResponse;
    return Array.isArray(payload.areas) ? payload.areas : [];
  }

  async listManageableAreas(workspaceId: string): Promise<WorkspaceAreaRef[]> {
    const response = await this.fetch(workspaceId, '/areas/manageable', {
      method: 'GET',
    });
    const payload = (await response.json()) as WorkspaceAreaListResponse;
    return Array.isArray(payload.areas) ? payload.areas : [];
  }

  async bootstrapAreas(workspaceId: string): Promise<WorkspaceAreaBootstrap> {
    const response = await this.fetch(workspaceId, '/areas/bootstrap', {
      method: 'GET',
    });
    const payload = (await response.json()) as Partial<WorkspaceAreaBootstrap>;
    return {
      areas: Array.isArray(payload.areas) ? payload.areas : [],
      manageableAreas: Array.isArray(payload.manageableAreas)
        ? payload.manageableAreas
        : [],
    };
  }

  async getAccessibleTeamspace(
    workspaceId: string,
    rootFolderId: string
  ): Promise<WorkspaceAreaRef | null> {
    const response = await this.fetch(
      workspaceId,
      `/areas/teamspaces/${encodeURIComponent(rootFolderId)}`,
      { method: 'GET' }
    );
    const payload = (await response.json()) as WorkspaceAreaResponse;
    return payload.area ?? null;
  }

  async createTeamspace(
    workspaceId: string,
    name: string,
    localMutationId?: string
  ): Promise<WorkspaceAreaRef> {
    const response = await this.fetch(workspaceId, '/areas/teamspaces', {
      method: 'POST',
      headers: mutationHeaders(localMutationId),
      body: JSON.stringify({ name }),
    });
    return (await response.json()) as WorkspaceAreaRef;
  }

  async updateTeamspace(
    workspaceId: string,
    rootFolderId: string,
    patch: { name?: string },
    localMutationId?: string
  ): Promise<WorkspaceAreaRef> {
    const response = await this.fetch(
      workspaceId,
      `/areas/teamspaces/${encodeURIComponent(rootFolderId)}`,
      {
        method: 'PATCH',
        headers: mutationHeaders(localMutationId),
        body: JSON.stringify(patch),
      }
    );
    return (await response.json()) as WorkspaceAreaRef;
  }

  async trashTeamspace(
    workspaceId: string,
    rootFolderId: string,
    localMutationId?: string
  ): Promise<void> {
    await this.fetch(
      workspaceId,
      `/areas/teamspaces/${encodeURIComponent(rootFolderId)}/trash`,
      {
        method: 'POST',
        headers: mutationHeaders(localMutationId, false),
      }
    );
  }

  private async fetch(
    workspaceId: string,
    suffix: string,
    init: RequestInit
  ): Promise<Response> {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('Workspace server is not available');
    }
    return defaultWorkspaceApiClient.request({
      workspaceId,
      baseUrl: server.serverMetadata.baseUrl,
      path: `/api/workspace/${encodeURIComponent(workspaceId)}${suffix}`,
      init,
      category: 'metadata',
      key: `${init.method ?? 'GET'}:${suffix}`,
      mode: init.method === 'GET' ? 'background' : 'foreground',
      priority: init.method === 'GET' ? 'visible' : 'foreground',
    });
  }
}

function mutationHeaders(
  localMutationId: string | undefined,
  includeContentType = true
): Record<string, string> {
  return {
    ...(includeContentType ? { 'content-type': 'application/json' } : {}),
    ...(localMutationId
      ? { 'x-streamlain-local-mutation-id': localMutationId }
      : {}),
  };
}
